import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import MarketingConsentSelect from "@/components/contact/MarketingConsentSelect";
import ContactsEmailAutoComplete from "@/components/form/components/ContactsEmailAutoComplete";
import { ContactsPhoneNumberAutoComplete } from "@/components/form/components/ContactsPhoneNumberAutoComplete";
import HiddenInput from "@/components/form/components/HiddenInput";
import LabelWithPopover from "@/components/form/labels/LabelWithPopover";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import AntIcon from "@/components/icons/AntIcon";
import { selectStandardProps } from "@/utils/componentUtils";
import { phoneNumberNormalizeFunction, setErrorsToForm } from "@/utils/formUtils";
import { isDefined } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { Button, Card, Col, Divider, Flex, Form, Radio, Row, Select, Space } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFillFinancialMediation, useSearchClient, useValidateClient } from "../../../common/api/queries";
import { rowGutter } from "../../../common/constants";
import { Permission } from "../../../common/security/enums";
import { hasAnyPermission, hasPermission } from "../../../common/security/utils";
import { FieldConstraintViolation } from "../../../common/types";
import { usePermissions } from "../../auth/hooks";
import ClientDrawerForm from "../../client/components/drawers/ClientDrawerForm";
import ClientRepresentativeSelectModal from "../../client/components/search/ClientRepresentativeSelectModal";
import ClientSearchInput from "../../client/components/search/ClientSearchInput";
import {
  CalcValidationGroup,
  ClientFormStage,
  ClientFormType,
  ClientRepresentativeFunction,
  ClientSearchActionType,
  ClientType,
} from "../../client/enums";
import { useClientSearch } from "../../client/hooks";
import { Client, ClientRepresentative, LegalClient } from "../../client/types";
import {
  getCreateUpdateContractClientFromClient,
  isLegalClient,
  processClientsDataViolations,
} from "../../client/utils";
import { ProductFinancialSector } from "../../product/enums";
import { FillFinancialMediation } from "../types";

enum ClientIndex {
  POLICY_HOLDER,
  REPRESENTATIVE,
}

const clientsIndicesMap = (): Map<ClientFormType.POLICY_HOLDER | ClientFormType.REPRESENTATIVE, ClientIndex> =>
  new Map([
    [ClientFormType.POLICY_HOLDER, ClientIndex.POLICY_HOLDER],
    [ClientFormType.REPRESENTATIVE, ClientIndex.REPRESENTATIVE],
  ]);

const getClientType = (type: ClientIndex) => {
  return type === ClientIndex.POLICY_HOLDER ? ClientFormType.POLICY_HOLDER : ClientFormType.REPRESENTATIVE;
};

const getClientTypeIndex = (type: ClientFormType.POLICY_HOLDER | ClientFormType.REPRESENTATIVE) => {
  return clientsIndicesMap().get(type) as ClientIndex;
};

export const FinancialMediationDocumentPrefill = () => {
  const { t } = useTranslation();

  const permissions = usePermissions();

  const [clientFormOpen, setClientFormOpen] = useState(false);
  const [isSelectRepresentativeModalOpen, setIsSelectRepresentativeModalOpen] = useState(false);
  const [processedClientIndex, setProcessedClientIndex] = useState<ClientIndex>();
  const [clientsViolationErrors, setClientsViolationErrors] = useState<
    Map<ClientFormType.POLICY_HOLDER | ClientFormType.REPRESENTATIVE, FieldConstraintViolation[]>
  >(() => new Map());

  const [form] = Form.useForm<FillFinancialMediation>();
  const sector = Form.useWatch("sector", form);
  const formClients = Form.useWatch("clients", form);

  const clientSearch = useClientSearch();
  const clientValidation = useValidateClient();
  const policyHolderRefetch = useSearchClient();

  const fillFinancialMediation = useFillFinancialMediation();

  useEffect(() => {
    return () => {
      clientSearch.onResultDelete();
    };
  }, []);

  const allowedSectors = useMemo(() => {
    const sectors: ProductFinancialSector[] = [];

    if (hasPermission(permissions, Permission.INSURANCE_READ)) {
      sectors.push(ProductFinancialSector.NON_LIFE_INSURANCES, ProductFinancialSector.LIFE_INSURANCES);
    }

    if (hasPermission(permissions, Permission.INVESTMENT_READ)) {
      sectors.push(ProductFinancialSector.CAPITAL_MARKET);
    }

    if (hasAnyPermission(permissions, [Permission.SECOND_PILLAR_READ, Permission.THIRD_PILLAR_READ])) {
      sectors.push(ProductFinancialSector.SENIOR_PENSION_SAVINGS);
    }

    return sectors;
  }, [permissions]);

  const handleClientChange = (clientIndex: ClientIndex, client?: Partial<Client>, stage?: ClientFormStage) => {
    if (stage !== ClientFormStage.NEW) {
      form.setFieldsValue({
        clients:
          clientIndex === ClientIndex.POLICY_HOLDER
            ? client
              ? [
                  { ...getCreateUpdateContractClientFromClient(client as Client), stage },
                  formClients[ClientIndex.REPRESENTATIVE],
                ].filter((item) => !!item)
              : []
            : [
                formClients[ClientIndex.POLICY_HOLDER],
                client ? { ...getCreateUpdateContractClientFromClient(client as Client), stage } : undefined,
              ].filter((item) => !!item),
      });
    } else {
      form.setFieldsValue({
        clients: [
          clientIndex === ClientIndex.POLICY_HOLDER
            ? { ...formClients[ClientIndex.POLICY_HOLDER], stage }
            : formClients[ClientIndex.POLICY_HOLDER],
          clientIndex === ClientIndex.REPRESENTATIVE
            ? { ...formClients[ClientIndex.REPRESENTATIVE], stage }
            : formClients[ClientIndex.REPRESENTATIVE],
        ].filter((item) => !!item),
      });
    }

    if (client) {
      clientValidation.mutate(
        {
          prefix: `clients[${clientIndex}]`,
          client: getCreateUpdateContractClientFromClient(client as Client),
          validationGroup: CalcValidationGroup.GENERATE,
        },
        {
          onError: (error) => {
            if (error.violations) {
              setClientsViolationErrors(
                new Map([
                  ...clientsViolationErrors,
                  ...processClientsDataViolations(clientsIndicesMap(), "clients", error.violations),
                ]),
              );
            }
          },
        },
      );
    }
  };

  useEffect(() => {
    if (!isDefined(processedClientIndex) || clientSearch.inProgress) {
      return;
    }

    if (clientSearch.result.data) {
      const client = clientSearch.result.data as Client;
      if (isLegalClient(client)) {
        handleClientChange(processedClientIndex, client, ClientFormStage.SELECTED);
        form.setFieldsValue({ policyHolderRepresentativeIndex: ClientIndex.REPRESENTATIVE });
      } else {
        handleClientChange(
          processedClientIndex,
          {
            ...clientSearch.result.data,
            identityCardNumber: undefined,
            previousIdentityCardNumber: client.identityCardNumber,
          },
          ClientFormStage.SELECTED,
        );
      }

      setClientFormOpen(true);
    } else if (!clientSearch.inProgress) {
      handleClientChange(processedClientIndex, undefined, ClientFormStage.NEW);
    }
  }, [clientSearch.result]);

  const clearState = (clientIndex: ClientIndex) => {
    setProcessedClientIndex(undefined);
    handleClientChange(clientIndex, undefined, undefined);

    setClientsViolationErrors((prevState) => {
      const newErrors = new Map(prevState);
      newErrors.delete(getClientType(clientIndex));

      return newErrors;
    });
  };

  const handleClientSearchActionClick = (
    type: ClientFormType.POLICY_HOLDER | ClientFormType.REPRESENTATIVE,
    actionType: ClientSearchActionType,
  ) => {
    switch (actionType) {
      case ClientSearchActionType.CREATE:
      case ClientSearchActionType.UPDATE:
        setClientFormOpen(true);
        setProcessedClientIndex(getClientTypeIndex(type));
        break;
      case ClientSearchActionType.DELETE:
        if (type === ClientFormType.POLICY_HOLDER) {
          form.setFieldsValue({
            policyHolderEmail: undefined,
            policyHolderPhone: undefined,
            policyHolderMarketingConsent: undefined,
          });

          clearState(ClientIndex.REPRESENTATIVE);
        }
        clearState(getClientTypeIndex(type));
        break;
    }
  };

  const handleClientSearchChange = (
    value: string,
    type: ClientFormType.POLICY_HOLDER | ClientFormType.REPRESENTATIVE,
  ) => {
    if (!value) {
      clearState(getClientTypeIndex(type));
      return;
    }

    form.validateFields([["clients", getClientTypeIndex(type), "identifier"]]).then(() => {
      setProcessedClientIndex(getClientTypeIndex(type));
      clientSearch.onSearch({
        keyword: value,
        clientType: type === ClientFormType.REPRESENTATIVE ? ClientType.NATURAL : undefined,
      });
    });
  };

  const handleClientFormSubmit = (
    client: Client,
    type: ClientFormType.POLICY_HOLDER | ClientFormType.REPRESENTATIVE,
  ) => {
    setClientFormOpen(false);
    setProcessedClientIndex(undefined);
    setClientsViolationErrors((prevState) => {
      const newErrors = new Map(prevState);
      newErrors.delete(type);

      return newErrors;
    });

    handleClientChange(getClientTypeIndex(type), client, ClientFormStage.SELECTED);
  };

  const handleRepresentativeSelect = (clientRepresentative: ClientRepresentative) => {
    setIsSelectRepresentativeModalOpen(false);

    form.setFieldsValue({
      policyHolderRepresentativeIndex: ClientIndex.REPRESENTATIVE,
      policyHolderRepresentativeFunction: clientRepresentative.function,
    });

    setProcessedClientIndex(ClientIndex.REPRESENTATIVE);

    handleClientChange(
      ClientIndex.REPRESENTATIVE,
      {
        ...clientRepresentative.representative,
        identityCardNumber: undefined,
        previousIdentityCardNumber: clientRepresentative.representative.identityCardNumber,
      },
      ClientFormStage.EXISTING,
    );

    setClientFormOpen(true);
  };

  const handleFormSubmit = () => {
    form.validateFields().then((values) => {
      fillFinancialMediation.mutate(values, {
        onError: (error) => {
          const clientsViolations = error.violations?.filter((violation) => violation.fieldPath.startsWith("clients"));

          if (clientsViolations?.length) {
            setClientsViolationErrors(
              (prevState) =>
                new Map([
                  ...prevState,
                  ...processClientsDataViolations(clientsIndicesMap(), "clients", clientsViolations),
                ]),
            );
          }
          setErrorsToForm(form, "financialMediation.attrs", error.violations);
        },
        onSuccess: () => {
          policyHolderRefetch.mutate(
            { keyword: values.clients[ClientIndex.POLICY_HOLDER]?.identifier ?? "" },
            {
              onSuccess: (result) => {
                if (result.data) {
                  const representative = form.getFieldValue(["clients", ClientIndex.REPRESENTATIVE]);
                  const client = {
                    ...getCreateUpdateContractClientFromClient(result.data as Client),
                    stage: ClientFormStage.SELECTED,
                  };

                  form.setFieldsValue({ clients: representative ? [client, representative] : [client] });
                }
              },
            },
          );
        },
      });
    });
  };

  return (
    <Card className="card-box">
      <Form form={form} layout="vertical" name="financialMediationDocumentPrefillForm">
        <HiddenInput name="policyHolderIndex" initialValue={ClientIndex.POLICY_HOLDER} />
        <HiddenInput name="policyHolderRepresentativeIndex" />
        <HiddenInput name="clients" initialValue={[]} />

        <Form.Item name="sector">
          <Radio.Group block optionType="button" size="large" buttonStyle="solid">
            {allowedSectors.map((sector) => (
              <Radio.Button value={sector} key={sector}>
                <b>{t("financialMediation.helpers.sectors." + sector)}</b>
              </Radio.Button>
            ))}
          </Radio.Group>
        </Form.Item>

        {sector ? (
          <div className="margin-top-large">
            <Card>
              <Row gutter={rowGutter}>
                <ClientSearchInput<ClientFormType.POLICY_HOLDER | ClientFormType.REPRESENTATIVE>
                  violationErrors={clientsViolationErrors}
                  inProgress={clientSearch.inProgress || policyHolderRefetch.isPending}
                  inputColSpan={6}
                  clientNameColSpan={6}
                  onActionClick={handleClientSearchActionClick}
                  onSearch={handleClientSearchChange}
                  onChange={handleClientSearchChange}
                  formItemProps={{
                    name: ["clients", ClientIndex.POLICY_HOLDER, "identifier"],
                    label: (
                      <LabelWithTooltip
                        tooltip={t("helpers.pinFormat")}
                        label={t("financialMediation.attrs.onlyPolicyHolder")}
                      />
                    ),
                    rules: [validations.notBlank, validations.pinOrCrn],
                  }}
                  formStage={formClients?.[ClientIndex.POLICY_HOLDER]?.stage}
                  formType={ClientFormType.POLICY_HOLDER}
                  client={formClients?.[ClientIndex.POLICY_HOLDER]?.client as Partial<Client>}
                  processedType={
                    processedClientIndex === ClientIndex.POLICY_HOLDER ? ClientFormType.POLICY_HOLDER : undefined
                  }
                />

                <Col span={12}>
                  <Card title={t("financialMediation.helpers.contact")} size="small">
                    <Row gutter={rowGutter}>
                      <Col span={8}>
                        <ContactsEmailAutoComplete
                          formItemProps={{
                            name: "policyHolderEmail",
                            label: t("financialMediation.attrs.mediationData.policyHolderData.email"),
                            rules: [validations.notBlank, validations.size(1, 254), validations.email],
                          }}
                          contacts={formClients[ClientIndex.POLICY_HOLDER]?.client?.contacts}
                        />
                      </Col>

                      <Col span={7}>
                        <ContactsPhoneNumberAutoComplete
                          formItemProps={{
                            name: "policyHolderPhone",
                            label: t("financialMediation.attrs.mediationData.policyHolderData.phone"),
                            rules: [validations.notBlank, validations.mobilePhoneNumber()],
                            normalize: phoneNumberNormalizeFunction,
                          }}
                          contacts={formClients[ClientIndex.POLICY_HOLDER]?.client?.contacts}
                        />
                      </Col>

                      <Col span={9}>
                        <MarketingConsentSelect
                          formItemProps={{
                            name: "policyHolderMarketingConsent",
                            label: (
                              <LabelWithPopover
                                label={t("contact.enums.marketingConsent._label")}
                                popoverTitle={t("financialMediation.helpers.marketingConsentDesc.title")}
                                popoverContent={
                                  <div style={{ maxWidth: 550, maxHeight: 450, overflowY: "scroll" }}>
                                    {[1, 2, 3, 4, 5].map((value) => (
                                      <p key={value}>
                                        {t(`financialMediation.helpers.marketingConsentDesc.p${value}`)}
                                      </p>
                                    ))}
                                  </div>
                                }
                              />
                            ),
                            rules: [validations.notNull],
                          }}
                        />
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
            </Card>

            {formClients[ClientIndex.POLICY_HOLDER]?.client?.type === ClientType.LEGAL && (
              <div className="margin-top-small">
                <Divider titlePlacement="start" className="divider-subheader">
                  {t("financialMediation.attrs.onlyPolicyHolderRepresentative")}
                </Divider>

                <div>
                  <Flex gap={rowGutter}>
                    {formClients[ClientIndex.POLICY_HOLDER]?.client.representatives.length > 0 && (
                      <SecondaryButton
                        className="form-item-without-label"
                        onClick={() => setIsSelectRepresentativeModalOpen(true)}
                      >
                        {t("financialMediation.actions.selectRepresentative")}
                      </SecondaryButton>
                    )}

                    <Form.Item
                      style={{ width: 180 }}
                      name="policyHolderRepresentativeFunction"
                      label={t("financialMediation.helpers.onlyPolicyHolderRepresentativeFunction")}
                      rules={[validations.notNull]}
                    >
                      <Select
                        {...selectStandardProps}
                        options={Object.keys(ClientRepresentativeFunction).map((func) => ({
                          value: func,
                          label: t("client.enums.representativeFunction." + func),
                        }))}
                      />
                    </Form.Item>

                    <ClientSearchInput<ClientFormType.POLICY_HOLDER | ClientFormType.REPRESENTATIVE>
                      violationErrors={clientsViolationErrors}
                      inProgress={clientSearch.inProgress || policyHolderRefetch.isPending}
                      inputColSpan={5}
                      clientNameColSpan={6}
                      onActionClick={handleClientSearchActionClick}
                      onSearch={handleClientSearchChange}
                      onChange={handleClientSearchChange}
                      formItemProps={{
                        name: ["clients", ClientIndex.REPRESENTATIVE, "identifier"],
                        label: (
                          <LabelWithTooltip
                            tooltip={t("helpers.pinFormat")}
                            label={t("financialMediation.attrs.onlyPolicyHolderRepresentative")}
                          />
                        ),
                        rules: [validations.notBlank, validations.pin],
                        validateFirst: true,
                      }}
                      formStage={formClients?.[ClientIndex.REPRESENTATIVE]?.stage}
                      formType={ClientFormType.REPRESENTATIVE}
                      client={formClients?.[ClientIndex.REPRESENTATIVE]?.client as Partial<Client>}
                      processedType={
                        processedClientIndex === ClientIndex.REPRESENTATIVE ? ClientFormType.REPRESENTATIVE : undefined
                      }
                    />
                  </Flex>
                </div>
              </div>
            )}

            <ClientDrawerForm
              open={clientFormOpen}
              client={
                isDefined(processedClientIndex)
                  ? (formClients[processedClientIndex]?.client as Partial<Client>)
                  : undefined
              }
              initialIdentifier={
                isDefined(processedClientIndex) ? formClients[processedClientIndex]?.identifier : undefined
              }
              requireIdCardNumber
              violationErrors={clientsViolationErrors}
              onFormSubmit={handleClientFormSubmit}
              formType={isDefined(processedClientIndex) ? getClientType(processedClientIndex) : undefined}
            />

            <ClientRepresentativeSelectModal
              open={isSelectRepresentativeModalOpen}
              client={
                formClients[ClientIndex.POLICY_HOLDER]?.client
                  ? (formClients[ClientIndex.POLICY_HOLDER].client as unknown as Partial<LegalClient>)
                  : undefined
              }
              onSelect={handleRepresentativeSelect}
              onFormCancel={() => setIsSelectRepresentativeModalOpen(false)}
            />
          </div>
        ) : (
          <div className="sub-header-info center-align">{t("financialMediation.helpers.sectorHint")}</div>
        )}
      </Form>

      {sector && (
        <Space className="margin-top-medium">
          <Button type="primary" icon={<AntIcon type="download" />} onClick={handleFormSubmit}>
            {t("common.download")}
          </Button>

          <Button
            icon={<AntIcon type="reload" />}
            onClick={() => {
              form.resetFields([
                "clients",
                "policyHolderIndex",
                "policyHolderEmail",
                "policyHolderPhone",
                "policyHolderMarketingConsent",
                "policyHolderRepresentativeIndex",
                "policyHolderRepresentativeFunction",
              ]);

              form.setFields([{ name: ["clients", ClientIndex.POLICY_HOLDER, "identifier"], errors: [] }]);

              setClientsViolationErrors(new Map());
            }}
          >
            {t("contractTermination.actions.resetForm")}
          </Button>
        </Space>
      )}
    </Card>
  );
};
